import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import Picker from '../../../common/components/picker/picker';
import AgentFace from '../../../common/components/agent-face/agent-face';
import {
    searchProjects,
    searchUsers,
    useIssueToken,
    useProjectRepositories,
} from '../contexts/tokens';
import { EMPTY_VALUES, NAME_MAX, TOKEN_KIND_OPTIONS, toIssuePayload, validate } from '../utils/form';

import '../styles/issue.css';

// Issuing a token, and showing it exactly once.
//
// THE FORM DISAPPEARS WHEN IT SUCCEEDS. What replaces it is the only moment the
// token exists in clear: the backend stores an HMAC and the prefix, so there is
// no second chance and no "show it again" button to write. A screen that went
// back to the list with a toast would be a screen that threw away the thing it
// just made.

export default function IssueTokenForm() {
    const navigate = useNavigate();
    const issue = useIssueToken();

    const [values, setValues] = useState(EMPTY_VALUES);
    const [errors, setErrors] = useState({});
    const [issued, setIssued] = useState(null);

    const repositories = useProjectRepositories(values.project_id);

    const change = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    // Changing project drops the repository: the one that was chosen belongs to
    // the project that is no longer selected, and sending it would tie the
    // token to a repo nobody meant.
    const changeProject = (id) => {
        setValues(prev => ({ ...prev, project_id: id, repository_id: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const found = validate(values);
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        try {
            setIssued(await issue.mutateAsync(toIssuePayload(values)));
        } catch (err) {
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo emitir el token');
        }
    };

    if (issued) return <IssuedToken token={issued} onDone={() => navigate('/admin/tokens')} />;

    const kind = TOKEN_KIND_OPTIONS.find(option => option.value === values.kind);

    return (
        <section className="issue">
            <header className="issue__header">
                <div>
                    <span className="issue__hint">Credenciales</span>
                    <h2 className="issue__title">Emitir un token</h2>
                </div>
                <Link to="/admin/tokens" className="issue__back">Volver a la lista</Link>
            </header>

            <form className="issue__form" onSubmit={handleSubmit}>
                <label className="issue__field">
                    <span className="issue__label">
                        Dueño <span className="issue__required">*</span>
                    </span>
                    <Picker
                        value={values.user_id}
                        onChange={(id) => change('user_id', id)}
                        fetchOptions={searchUsers}
                        cacheKey="users"
                        labelOf={user => user.name}
                        subtitleOf={user => user.email}
                        placeholder="Elegir persona..."
                        disabled={issue.isPending}
                        invalid={!!errors.user_id}
                    />
                    {errors.user_id
                        ? <span className="issue__error">{errors.user_id}</span>
                        : (
                            <span className="issue__note">
                                Un agente no es una persona: actúa <strong>en nombre de</strong>{' '}
                                una. Lo que este token haga queda firmado con esta cuenta.
                            </span>
                        )}
                </label>

                <label className="issue__field">
                    <span className="issue__label">
                        Nombre <span className="issue__required">*</span>
                    </span>
                    <input
                        className="issue__input"
                        value={values.name}
                        onChange={e => change('name', e.target.value)}
                        maxLength={NAME_MAX}
                        placeholder="agente-backend"
                        disabled={issue.isPending}
                    />
                    {errors.name
                        ? <span className="issue__error">{errors.name}</span>
                        : (
                            <span className="issue__note">
                                Es lo único con lo que se lo reconoce después: el token en
                                claro no se puede volver a ver.
                            </span>
                        )}
                </label>

                <fieldset className="issue__field issue__field--wide">
                    <legend className="issue__label">
                        Qué puede hacer <span className="issue__required">*</span>
                    </legend>
                    <div className="issue__kinds">
                        {TOKEN_KIND_OPTIONS.map(option => (
                            <label
                                key={option.value}
                                className={`kind${values.kind === option.value ? ' kind--on' : ''}`}
                            >
                                <input
                                    type="radio"
                                    name="kind"
                                    value={option.value}
                                    checked={values.kind === option.value}
                                    onChange={e => change('kind', e.target.value)}
                                    disabled={issue.isPending}
                                    className="kind__radio"
                                />
                                <AgentFace
                                    state={values.kind === option.value ? 'working' : 'idle'}
                                    kind={option.value}
                                    size={32}
                                />
                                <span className="kind__body">
                                    <span className="kind__name">{option.label}</span>
                                    <span className="kind__detail">{option.detail}</span>
                                </span>
                            </label>
                        ))}
                    </div>
                    {errors.kind && <span className="issue__error">{errors.kind}</span>}
                    <span className="issue__note">
                        {/* The role is the token's, not the person's. It is the one
                            thing about this form that surprises everybody once. */}
                        El permiso sale de acá y <strong>no</strong> del rol de la persona:
                        el backend lee el tipo del token para decidir qué puede hacer. Un
                        webmaster con un token <code>{kind?.value}</code> puede lo que puede
                        ese token, y nada más.
                    </span>
                </fieldset>

                <label className="issue__field">
                    <span className="issue__label">Proyecto</span>
                    <Picker
                        value={values.project_id}
                        onChange={changeProject}
                        fetchOptions={searchProjects}
                        cacheKey="projects"
                        labelOf={project => project.name}
                        subtitleOf={project => project.slug}
                        placeholder="Todos los proyectos..."
                        disabled={issue.isPending}
                    />
                    <span className="issue__note">
                        El token <strong>no guarda proyecto</strong>. Esto solo sirve para
                        encontrar el repositorio: la lista de repositorios no se puede
                        buscar por texto, solo filtrar por proyecto.
                    </span>
                </label>

                <label className="issue__field">
                    <span className="issue__label">Repositorio</span>
                    <select
                        className="issue__input"
                        value={values.repository_id}
                        onChange={e => change('repository_id', e.target.value)}
                        disabled={issue.isPending || !values.project_id}
                    >
                        <option value="">Sin repositorio</option>
                        {(repositories.data ?? []).map(repository => (
                            <option key={repository.id} value={repository.id}>
                                {repository.name}
                            </option>
                        ))}
                    </select>
                    {errors.repository_id
                        ? <span className="issue__error">{errors.repository_id}</span>
                        : (
                            <span className="issue__note issue__note--warn">
                                {/* Verified against the running backend, twice: a token
                                    issued for repository 1 answers with every repository
                                    its owner can reach. It is finding H15.6-c. */}
                                Queda <strong>registrado</strong> en el token, y hoy{' '}
                                <strong>no angosta nada</strong>: el contrato dice que lo
                                restringe a ese repositorio, y el alcance real sigue saliendo
                                del equipo al que pertenece el dueño. Hallazgo H15.6-c, abierto
                                en el tier 2.
                            </span>
                        )}
                </label>

                <label className="issue__field">
                    <span className="issue__label">Vence</span>
                    <input
                        type="datetime-local"
                        className="issue__input"
                        value={values.expires_at}
                        onChange={e => change('expires_at', e.target.value)}
                        disabled={issue.isPending}
                    />
                    {errors.expires_at
                        ? <span className="issue__error">{errors.expires_at}</span>
                        : (
                            <span className="issue__note">
                                Vacío es un token que no vence. Para una corrida de una tarde,
                                ponerle hora es la diferencia entre una credencial y una
                                credencial olvidada.
                            </span>
                        )}
                </label>

                <div className="issue__actions">
                    <button type="submit" className="issue__submit" disabled={issue.isPending}>
                        {issue.isPending ? 'Emitiendo…' : 'Emitir token'}
                    </button>
                    <Link to="/admin/tokens" className="issue__cancel">Cancelar</Link>
                </div>
            </form>
        </section>
    );
}

function IssuedToken({ token, onDone }) {
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(token.token);
            setCopied(true);
        } catch {
            // Copying can be refused — an insecure origin, a browser policy —
            // and the token is on screen either way. Saying so beats a button
            // that looks like it worked.
            toast.error('El navegador no dejó copiar. Seleccionalo y copialo a mano.');
        }
    };

    return (
        <section className="issued">
            <h2 className="issued__title">Este token se muestra una sola vez</h2>

            <p className="issued__lead">
                El servidor no lo guarda: lo que conserva es un hash y el prefijo{' '}
                <code>{token.token_prefix}</code>. Si se pierde, no se recupera — se revoca y
                se emite otro.
            </p>

            <div className="issued__box">
                <code className="issued__secret">{token.token}</code>
                <button type="button" className="issued__copy" onClick={copy}>
                    {copied ? 'Copiado' : 'Copiar'}
                </button>
            </div>

            <dl className="issued__meta">
                <div><dt>Nombre</dt><dd>{token.name}</dd></div>
                <div><dt>Tipo</dt><dd><code>{token.kind}</code></dd></div>
                <div>
                    <dt>Repositorio</dt>
                    <dd>{token.repository_id ? `#${token.repository_id}` : 'ninguno'}</dd>
                </div>
                <div>
                    <dt>Vence</dt>
                    <dd>{token.expires_at ? new Date(token.expires_at).toLocaleString('es-CO') : 'nunca'}</dd>
                </div>
            </dl>

            <p className="issued__how">
                El agente lo manda en la cabecera <code>Authorization: Bearer …</code>. No
                viaja por cookie y no lo manda el navegador solo — por eso las rutas de
                agente no llevan CSRF.
            </p>

            <button type="button" className="issued__done" onClick={onDone}>
                Ya lo guardé
            </button>
        </section>
    );
}
